/**
 * O estado de um bloco: carregando · pronto · erro — e NUNCA os três misturados
 * (lote P2 · achado PORTAL-04).
 *
 * ## O defeito que isto fecha
 *
 * A versão anterior guardava `campanhas`, `pedidos` e `documentos` em três `useState`
 * inicializados com `[]`, e um `erro` à parte. Enquanto o pedido estava no ar, a tela
 * mostrava lista vazia com KPI "R$ 0,00" — um fato financeiro FALSO, indistinguível de
 * "esta empresa não tem campanhas". E, ao trocar o filtro, as linhas antigas ficavam na
 * tela sob o rótulo novo até a resposta chegar: o usuário lia os números de julho com o
 * cabeçalho de agosto.
 *
 * Com uma união discriminada, "não sei ainda" é um estado que existe, e a tela é
 * obrigada a tratá-lo — não dá para ler `.dados` sem antes provar que está `pronto`.
 *
 * ## A troca de filtro SUSPENDE
 *
 * Mudar a chave devolve `carregando` no mesmo render, antes de qualquer resposta. É a
 * diferença entre "esperando" e "isto é o resultado do filtro novo".
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { FalhaDoPortal, type TipoDeFalha } from '../api/erros.js'

/**
 * Quem avisar quando a sessao cair.
 *
 * A queda de sessao nao e um erro DE BLOCO: e o fim do acesso, e a resposta certa e sair
 * do portal inteiro — nao mostrar um cartao vermelho com o cabecalho da empresa ainda na
 * tela. Cada bloco faz o seu proprio pedido e qualquer um pode receber 401; em vez de
 * espalhar essa decisao por sete lugares, ela sobe por aqui, uma vez.
 *
 * E um registro de modulo, e nao um contexto de React, porque quem precisa reagir e a
 * casca — que esta ACIMA de todos os blocos. Passar um adereco ate la atravessaria cada
 * componente do caminho com uma responsabilidade que nao e dele.
 */
let avisarQuedaDeSessao: (() => void) | null = null

export function aoPerderSessao(ouvinte: (() => void) | null): void {
  avisarQuedaDeSessao = ouvinte
}

export type Recurso<T> =
  | { readonly estado: 'carregando' }
  | { readonly estado: 'pronto'; readonly dados: T }
  | { readonly estado: 'erro'; readonly tipo: TipoDeFalha }

export interface Consulta<T> {
  readonly recurso: Recurso<T>
  readonly recarregar: () => void
}

/**
 * @param chave  muda ⇒ o bloco volta a `carregando` IMEDIATAMENTE. É a string que
 *               descreve o pedido (rota + filtros); duas telas com a mesma chave pedem
 *               a mesma coisa.
 * @param buscar recebe o sinal de cancelamento: resposta atrasada de um filtro que já
 *               não está na tela é DESCARTADA, nunca renderizada.
 */
export function useConsulta<T>(chave: string, buscar: (sinal: AbortSignal) => Promise<T>): Consulta<T> {
  const daVez = useRef(buscar)
  daVez.current = buscar

  const [recurso, setRecurso] = useState<Recurso<T>>({ estado: 'carregando' })
  const [tentativa, setTentativa] = useState(0)

  useEffect(() => {
    const controle = new AbortController()
    let vivo = true
    setRecurso({ estado: 'carregando' })

    daVez
      .current(controle.signal)
      .then((dados) => {
        if (vivo) setRecurso({ estado: 'pronto', dados })
      })
      .catch((causa: unknown) => {
        const tipo = causa instanceof FalhaDoPortal ? causa.tipo : 'servidor'
        // A sessao caiu: avisa a casca ANTES de qualquer estado de bloco. O cartao de
        // erro deste bloco nem chega a aparecer, porque o portal inteiro sai de cena.
        if (tipo === 'sessao') avisarQuedaDeSessao?.()
        if (!vivo) return
        /*
          O recorte mudou na sessão (D-131): a casca já foi avisada pelo cliente HTTP e vai
          remontar a tela no recorte novo. O bloco continua "carregando" até lá — um cartão de
          erro aqui seria um piscar vermelho sobre uma troca que está dando certo.
        */
        if (tipo === 'contexto') return
        setRecurso({ estado: 'erro', tipo })
      })

    return () => {
      vivo = false
      controle.abort()
    }
  }, [chave, tentativa])

  const recarregar = useCallback(() => {
    setTentativa((n) => n + 1)
  }, [])

  return { recurso, recarregar }
}

/**
 * Junta blocos: enquanto QUALQUER um carrega, o conjunto carrega; o primeiro erro
 * ganha.
 *
 * A cascata é deliberada. Um KPI somado sobre metade dos blocos é um número errado com
 * cara de certo — e é justamente por isso que os três pedidos da tela do cliente eram um
 * `Promise.all`: eles formam UM retrato, e retrato pela metade não é retrato.
 */
export function juntar(recursos: readonly Recurso<unknown>[]): 'carregando' | 'pronto' | TipoDeFalha {
  const comErro = recursos.find((r) => r.estado === 'erro')
  if (comErro?.estado === 'erro') return comErro.tipo
  if (recursos.some((r) => r.estado === 'carregando')) return 'carregando'
  return 'pronto'
}
